import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, MapPin, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Check, X, RefreshCw, Search, ImageIcon, Upload, Globe, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';
import { SidePanel } from '../components/SidePanel';
import { PhoneInput } from '../components/PhoneInput';
import { TZ_OPTIONS } from '../utils/dateUtils';

const EMPTY_SOC = { nom: '', siret: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '', odoo_company_id: null, entreprise_id: '', entreprise_nom: '', filtrer_parc: false, timezone: 'Europe/Paris', numero_compte_client: '', numero_compte_fourn: '' };
const EMPTY_AGC = { societe_id: '', nom: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '', responsable: '', odoo_company_id: null, entreprise_id: '', entreprise_nom: '', filtrer_parc: false, filtrer_controles: false, logo_b64: '' };

// ── Picker entreprise Annuaire ────────────────────────────────────────────────
function EntreprisePicker({ value, valueNom, onChange }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.getEntreprises(search.trim() ? { search } : {});
        setResults(Array.isArray(res.data) ? res.data.slice(0, 30) : []);
      } catch {} finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open]);

  const select = (ent) => {
    onChange(ent?.entreprise_id ?? '', ent?.nom ?? '');
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={ref}>
      {value ? (
        <div className="flex items-center gap-2 w-full border rounded-lg px-3 py-2.5 text-sm" style={{ borderColor: 'var(--border)' }}>
          <Building2 size={14} style={{ color: 'var(--primary-light)', flexShrink: 0 }} />
          <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{valueNom}</span>
          <button type="button" onClick={() => select(null)}
            className="p-0.5 hover:bg-red-50 rounded flex-shrink-0" style={{ color: '#DC2626' }}>
            <X size={13} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => { setSearch(''); setOpen(true); }}
          className="flex items-center gap-2 w-full border rounded-lg px-3 py-2.5 text-sm text-left"
          style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
          <Search size={14} style={{ flexShrink: 0 }} />
          Rechercher une entreprise de l'annuaire…
        </button>
      )}
      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 w-full bg-white rounded-xl border shadow-xl overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          <div className="p-2 border-b" style={{ borderColor: 'var(--border)' }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Nom de l'entreprise…"
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none"
              style={{ borderColor: 'var(--primary-solid)' }} />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {loading
              ? <p className="px-4 py-3 text-sm text-center" style={{ color: 'var(--text-placeholder)' }}>Recherche…</p>
              : results.length === 0
                ? <p className="px-4 py-3 text-sm text-center" style={{ color: 'var(--text-placeholder)' }}>Aucune entreprise trouvée</p>
                : results.map(e => (
                  <button key={e.entreprise_id} type="button" onMouseDown={() => select(e)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2 border-b last:border-0 text-sm"
                    style={{ borderColor: '#F2F2EE' }}>
                    <Building2 size={13} style={{ color: 'var(--primary-light)', flexShrink: 0 }} />
                    <span className="truncate" style={{ color: 'var(--text)' }}>{e.nom}</span>
                  </button>
                ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Logo uploader ─────────────────────────────────────────────────────────────
function LogoUploader({ value, onChange }) {
  const fileRef = useRef(null);
  const MAX_SIZE = 1.5 * 1024 * 1024; // 1.5 MB

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Veuillez sélectionner une image (PNG, JPEG, WebP).'); return; }
    if (file.size > MAX_SIZE) { alert('Image trop lourde — max 1,5 Mo.'); return; }
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div>
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Logo agence"
            data-testid="logo-preview"
            className="rounded-xl border object-contain"
            style={{ maxHeight: 80, maxWidth: 200, minWidth: 80, minHeight: 40, background: '#F9FAFB', borderColor: 'var(--border)' }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            title="Supprimer le logo"
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow"
            style={{ background: '#EF4444', color: 'white' }}>
            <X size={10} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          data-testid="logo-upload-zone"
          className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed cursor-pointer transition-colors hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', height: 72, minWidth: 160 }}>
          <Upload size={16} style={{ color: 'var(--text-disabled)' }} />
          <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
            Cliquez ou glissez un logo<br />
            <span style={{ color: 'var(--text-disabled)', fontSize: 10 }}>PNG, JPEG, WebP — max 1,5 Mo</span>
          </p>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={e => handleFile(e.target.files[0])}
        data-testid="logo-file-input"
      />
    </div>
  );
}

export default function SocietesAgences() {
  const { societes, reload } = useSociete();
  const [expanded, setExpanded] = useState({});
  const [socPanel, setSocPanel] = useState(false);
  const [agcPanel, setAgcPanel] = useState(false);
  const [editingSoc, setEditingSoc] = useState(null);
  const [editingAgc, setEditingAgc] = useState(null);
  const [socForm, setSocForm] = useState(EMPTY_SOC);
  const [agcForm, setAgcForm] = useState(EMPTY_AGC);
  const [saving, setSaving] = useState(false);
  const [odooCompanies, setOdooCompanies] = useState([]);
  const [odooLoading, setOdooLoading] = useState(false);

  // Partage de parc inter-agences
  const [parcExpanded, setParcExpanded] = useState({});
  const [autorisations, setAutorisations] = useState({}); // { [agence_proprio_id]: Set<agence_utilisatrice_id> }

  const loadAutorisations = useCallback(async () => {
    try {
      const r = await api.getAllAutorisations();
      const map = {};
      for (const [proprio, utilisatrices] of Object.entries(r.data || {})) {
        map[proprio] = new Set(utilisatrices);
      }
      setAutorisations(map);
    } catch {}
  }, []);

  useEffect(() => { loadAutorisations(); }, [loadAutorisations]);

  const toggleAutorisation = async (ownerId, utilisatriceId, currentlyEnabled) => {
    try {
      if (currentlyEnabled) {
        await api.removeAutorisation(ownerId, utilisatriceId);
        setAutorisations(prev => {
          const next = { ...prev, [ownerId]: new Set(prev[ownerId] || []) };
          next[ownerId].delete(utilisatriceId);
          return next;
        });
      } else {
        await api.addAutorisation(ownerId, utilisatriceId);
        setAutorisations(prev => {
          const next = { ...prev, [ownerId]: new Set(prev[ownerId] || []) };
          next[ownerId].add(utilisatriceId);
          return next;
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const loadOdooCompanies = useCallback(async () => {
    setOdooLoading(true);
    try {
      const res = await api.pingOdoo();
      setOdooCompanies(res.data.companies || []);
    } catch {
      toast.error('Impossible de contacter Odoo');
    } finally {
      setOdooLoading(false);
    }
  }, []);

  // Charger les sociétés Odoo à l'ouverture d'un panel
  useEffect(() => {
    if (socPanel || agcPanel) loadOdooCompanies();
  }, [socPanel, agcPanel, loadOdooCompanies]);

  const openNewSoc = () => { setEditingSoc(null); setSocForm(EMPTY_SOC); setSocPanel(true); };
  const openEditSoc = (s) => {
    setEditingSoc(s);
    setSocForm({ nom: s.nom || '', siret: s.siret || '', adresse: s.adresse || '',
      code_postal: s.code_postal || '', ville: s.ville || '', email: s.email || '',
      telephone: s.telephone || '', odoo_company_id: s.odoo_company_id || null,
      entreprise_id: s.entreprise_id || '', entreprise_nom: s.entreprise_nom || '',
      filtrer_parc: s.filtrer_parc || false, timezone: s.timezone || 'Europe/Paris',
      numero_compte_client: s.numero_compte_client || '',
      numero_compte_fourn: s.numero_compte_fourn || '' });
    setSocPanel(true);
  };
  const openNewAgc = (societe_id) => {
    setEditingAgc(null);
    setAgcForm({ ...EMPTY_AGC, societe_id });
    setAgcPanel(true);
  };
  const openEditAgc = (a) => {
    setEditingAgc(a);
    setAgcForm({ societe_id: a.societe_id, nom: a.nom || '', adresse: a.adresse || '',
      code_postal: a.code_postal || '', ville: a.ville || '', email: a.email || '',
      telephone: a.telephone || '', responsable: a.responsable || '',
      odoo_company_id: a.odoo_company_id || null,
      entreprise_id: a.entreprise_id || '', entreprise_nom: a.entreprise_nom || '',
      filtrer_parc: a.filtrer_parc || false, filtrer_controles: a.filtrer_controles || false, logo_b64: a.logo_b64 || '' });
    setAgcPanel(true);
  };

  const saveSoc = async () => {
    if (!socForm.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      editingSoc
        ? await api.updateSociete(editingSoc.societe_id, socForm)
        : await api.createSociete(socForm);
      toast.success(editingSoc ? 'Société mise à jour' : 'Société créée');
      setSocPanel(false);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
    }
    finally { setSaving(false); }
  };

  const saveAgc = async () => {
    if (!agcForm.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      editingAgc
        ? await api.updateAgence(editingAgc.agence_id, agcForm)
        : await api.createAgence(agcForm);
      toast.success(editingAgc ? 'Agence mise à jour' : 'Agence créée');
      setAgcPanel(false);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
    }
    finally { setSaving(false); }
  };

  const deleteSoc = async (id) => {
    if (!window.confirm('Supprimer cette société et toutes ses agences ?')) return;
    try { await api.deleteSociete(id); toast.success('Société supprimée'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const deleteAgc = async (id) => {
    if (!window.confirm('Supprimer cette agence ?')) return;
    try { await api.deleteAgence(id); toast.success('Agence supprimée'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const inp = "w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const lbl = "block text-xs font-medium mb-1";
  const lblSt = { color: 'var(--text-muted)' };

  return (
    <div className="space-y-6" data-testid="societes-agences-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Sociétés & Agences
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Structurez votre organisation en sociétés et agences
          </p>
        </div>
        <button onClick={openNewSoc} data-testid="add-societe-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={16} /> Nouvelle société
        </button>
      </div>

      {societes.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: 'var(--border)' }}>
          <Building2 size={40} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="font-semibold mb-1" style={{ color: 'var(--text)',  }}>Aucune société</p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            Créez votre première société pour structurer vos données
          </p>
          <button onClick={openNewSoc}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={15} /> Créer une société
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {societes.map(soc => (
            <div key={soc.societe_id} className="bg-white rounded-xl border overflow-hidden"
              style={{ borderColor: 'var(--border)' }}>
              {/* Ligne société */}
              <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(e => ({ ...e, [soc.societe_id]: !e[soc.societe_id] }))}>
                <div className="flex items-center gap-3">
                  {expanded[soc.societe_id]
                    ? <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />
                    : <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: 'var(--primary-50)' }}>
                    <Building2 size={15} style={{ color: 'var(--primary-solid)' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--text)',  }}>{soc.nom}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {(soc.agences || []).length} agence{(soc.agences || []).length !== 1 ? 's' : ''}
                      {soc.ville ? ` · ${soc.ville}` : ''}
                      {soc.siret ? ` · SIRET: ${soc.siret}` : ''}
                      {soc.odoo_company_id ? <span className="ml-1 px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: '#EDE9FE', color: '#6D28D9' }}>Odoo #{soc.odoo_company_id}</span> : null}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => openNewAgc(soc.societe_id)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                    <MapPin size={12} /> + Agence
                  </button>
                  <button onClick={() => openEditSoc(soc)}
                    className="p-1.5 rounded-md hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteSoc(soc.societe_id)}
                    className="p-1.5 rounded-md hover:bg-red-50" style={{ color: '#DC2626' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Agences */}
              {expanded[soc.societe_id] && (
                <div className="border-t" style={{ borderColor: 'var(--border-light)' }}>
                  {(soc.agences || []).length === 0 ? (
                    <div className="px-5 py-4 text-sm flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                      <MapPin size={13} />
                      Aucune agence —{' '}
                      <button onClick={() => openNewAgc(soc.societe_id)}
                        className="font-semibold hover:underline" style={{ color: 'var(--primary-solid)' }}>
                        en créer une
                      </button>
                    </div>
                  ) : (
                    soc.agences.map((agc, idx) => {
                      // Toutes les agences de l'org sauf celle-ci
                      const allAgences = (societes || []).flatMap(s => s.agences || []);
                      const otherAgences = allAgences.filter(a => a.agence_id !== agc.agence_id);
                      const autorisees = autorisations[agc.agence_id] || new Set();
                      const parcOpen = parcExpanded[agc.agence_id] || false;
                      return (
                        <div key={agc.agence_id}
                          style={{ borderBottom: idx < soc.agences.length - 1 ? `1px solid var(--border-light)` : 'none', background: 'var(--bg)' }}>
                          {/* Ligne principale */}
                          <div className="flex items-center justify-between px-5 py-3">
                            <div className="flex items-center gap-3">
                              <MapPin size={13} style={{ color: 'var(--primary-light)' }} />
                              {agc.logo_b64 ? (
                                <img src={agc.logo_b64} alt={agc.nom}
                                  className="rounded object-contain flex-shrink-0"
                                  style={{ height: 28, maxWidth: 56, background: '#F9FAFB', border: '1px solid var(--border)' }} />
                              ) : (
                                <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                                  style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
                                  <ImageIcon size={12} style={{ color: 'var(--text-disabled)' }} />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{agc.nom}</p>
                                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                  {[agc.ville, agc.responsable].filter(Boolean).join(' · ') || 'Aucune info'}
                                  {agc.odoo_company_id ? <span className="ml-1 px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: '#EDE9FE', color: '#6D28D9' }}>Odoo #{agc.odoo_company_id}</span> : null}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {otherAgences.length > 0 && (
                                <button
                                  data-testid={`parc-partage-btn-${agc.agence_id}`}
                                  onClick={() => setParcExpanded(p => ({ ...p, [agc.agence_id]: !parcOpen }))}
                                  title="Partage de parc"
                                  className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
                                  style={{
                                    background: parcOpen ? 'var(--primary-50, #EFF6FF)' : autorisees.size > 0 ? '#FFF7ED' : 'transparent',
                                    color: parcOpen ? 'var(--primary-solid)' : autorisees.size > 0 ? '#C2410C' : 'var(--text-muted)',
                                    border: `1px solid ${parcOpen ? 'var(--primary-solid)' : autorisees.size > 0 ? '#FED7AA' : 'var(--border-light)'}`,
                                  }}>
                                  <Share2 size={11} />
                                  {autorisees.size > 0 ? `${autorisees.size} agence${autorisees.size > 1 ? 's' : ''}` : 'Partage'}
                                </button>
                              )}
                              <button onClick={() => openEditAgc(agc)}
                                className="p-1.5 rounded-md hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => deleteAgc(agc.agence_id)}
                                className="p-1.5 rounded-md hover:bg-red-50" style={{ color: '#DC2626' }}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>

                          {/* Section partage de parc */}
                          {parcOpen && otherAgences.length > 0 && (
                            <div className="px-5 pb-3 pt-0"
                              style={{ background: '#FAFBFF', borderTop: '1px solid var(--border-light)' }}>
                              <p className="text-xs font-semibold mb-2 mt-2" style={{ color: 'var(--text-muted)' }}>
                                Agences pouvant utiliser le parc de <strong style={{ color: 'var(--text)' }}>{agc.nom}</strong>
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {otherAgences.map(other => {
                                  const enabled = autorisees.has(other.agence_id);
                                  return (
                                    <button
                                      key={other.agence_id}
                                      data-testid={`autorisation-toggle-${agc.agence_id}-${other.agence_id}`}
                                      onClick={() => toggleAutorisation(agc.agence_id, other.agence_id, enabled)}
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all"
                                      style={{
                                        background: enabled ? '#FFF7ED' : 'white',
                                        color: enabled ? '#C2410C' : 'var(--text-muted)',
                                        borderColor: enabled ? '#FED7AA' : 'var(--border-light)',
                                      }}>
                                      <div className="w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0"
                                        style={{ background: enabled ? '#C2410C' : '#E5E7EB' }}>
                                        {enabled && <Check size={9} color="white" />}
                                      </div>
                                      {other.nom}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Panel société */}
      <SidePanel open={socPanel} onOpenChange={setSocPanel}
        title={editingSoc ? 'Modifier la société' : 'Nouvelle société'}
        footer={
          <div className="flex gap-3">
            <button onClick={saveSoc} disabled={saving}
              data-testid="save-societe-btn"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--primary-gradient)' }}>
              <Check size={14} /> {saving ? 'Enregistrement…' : (editingSoc ? 'Mettre à jour' : 'Créer')}
            </button>
            <button onClick={() => setSocPanel(false)}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
              Annuler
            </button>
          </div>
        }>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Nom de la société *</label>
            <input value={socForm.nom} onChange={e => setSocForm(f => ({ ...f, nom: e.target.value }))}
              placeholder="Ex: MBud SAS" className={inp} style={inpSt} data-testid="societe-nom-input" />
          </div>
          <div>
            <label className={lbl} style={lblSt}>SIRET</label>
            <input value={socForm.siret} onChange={e => setSocForm(f => ({ ...f, siret: e.target.value }))}
              placeholder="12345678900000" className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Téléphone</label>
            <PhoneInput value={socForm.telephone} onChange={e => setSocForm(f => ({ ...f, telephone: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Email</label>
            <input type="email" value={socForm.email} onChange={e => setSocForm(f => ({ ...f, email: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Adresse</label>
            <input value={socForm.adresse} onChange={e => setSocForm(f => ({ ...f, adresse: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Code postal</label>
            <input value={socForm.code_postal} onChange={e => setSocForm(f => ({ ...f, code_postal: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Ville</label>
            <input value={socForm.ville} onChange={e => setSocForm(f => ({ ...f, ville: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          {/* Fuseau horaire */}
          <div className="col-span-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <label className={lbl} style={lblSt}>
              <Globe size={12} className="inline mr-1" />
              Fuseau horaire
            </label>
            <select
              value={socForm.timezone || 'Europe/Paris'}
              onChange={e => setSocForm(f => ({ ...f, timezone: e.target.value }))}
              className={inp} style={inpSt}
              data-testid="societe-timezone-select">
              {TZ_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
          {/* Intégration Odoo */}
          <div className="col-span-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <label className={lbl} style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                Société Odoo associée
                <span className="ml-1 font-normal opacity-70">(optionnel)</span>
              </label>
              <button type="button" onClick={loadOdooCompanies} disabled={odooLoading}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded-md border"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                <RefreshCw size={11} className={odooLoading ? 'animate-spin' : ''} />
                {odooLoading ? 'Chargement…' : 'Actualiser'}
              </button>
            </div>
            <select
              value={socForm.odoo_company_id}
              onChange={e => setSocForm(f => ({ ...f, odoo_company_id: e.target.value ? parseInt(e.target.value) : null }))}
              className={inp} style={inpSt}
              data-testid="societe-odoo-company-id-input">
              <option value="">— Aucune correspondance Odoo —</option>
              {odooCompanies.map(c => (
                <option key={c.id} value={c.id}>{c.name} (id: {c.id})</option>
              ))}
            </select>
          </div>
          {/* Lien Annuaire */}
          <div className="col-span-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <label className={lbl} style={lblSt}>
              Entreprise annuaire liée
              <span className="ml-1 font-normal opacity-70">(optionnel)</span>
            </label>
            <EntreprisePicker
              value={socForm.entreprise_id}
              valueNom={socForm.entreprise_nom}
              onChange={(id, nom) => setSocForm(f => ({ ...f, entreprise_id: id, entreprise_nom: nom }))}
            />
            {socForm.entreprise_id && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                <input type="checkbox"
                  checked={socForm.filtrer_parc}
                  onChange={e => setSocForm(f => ({ ...f, filtrer_parc: e.target.checked }))}
                  className="w-4 h-4 rounded cursor-pointer"
                  style={{ accentColor: 'var(--primary-solid)' }} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Filtrer le parc matériel sur cet exploitant
                </span>
              </label>
            )}
            {/* Comptes auxiliaires PennyLane — toujours visibles */}
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>
                Comptes auxiliaires PennyLane (intragroupe)
              </p>
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                Plage réservée : <code className="font-mono" style={{ background: 'var(--bg-subtle)', padding: '1px 4px', borderRadius: 3 }}>00001 – 00099</code>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>N° compte Client</label>
                  <div className="flex">
                    <span className="text-xs font-mono font-bold px-2 py-1.5 rounded-l-md border border-r-0 flex items-center"
                      style={{ background: '#EFF6FF', color: '#1D4ED8', borderColor: 'var(--border)' }}>4111</span>
                    <input
                      value={(socForm.numero_compte_client || '').replace(/^4111/, '')}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setSocForm(f => ({ ...f, numero_compte_client: v ? `4111${v}` : '' }));
                      }}
                      placeholder="00001"
                      maxLength={5}
                      data-testid="soc-numero-compte-client"
                      className="flex-1 px-2 py-1.5 rounded-r-md border text-xs font-mono"
                      style={{ borderColor: 'var(--border)', minWidth: 0 }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>N° compte Fournisseur</label>
                  <div className="flex">
                    <span className="text-xs font-mono font-bold px-2 py-1.5 rounded-l-md border border-r-0 flex items-center"
                      style={{ background: '#F0FDF4', color: '#15803D', borderColor: 'var(--border)' }}>4011</span>
                    <input
                      value={(socForm.numero_compte_fourn || '').replace(/^4011/, '')}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setSocForm(f => ({ ...f, numero_compte_fourn: v ? `4011${v}` : '' }));
                      }}
                      placeholder="00001"
                      maxLength={5}
                      data-testid="soc-numero-compte-fourn"
                      className="flex-1 px-2 py-1.5 rounded-r-md border text-xs font-mono"
                      style={{ borderColor: 'var(--border)', minWidth: 0 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidePanel>

      {/* Panel agence */}
      <SidePanel open={agcPanel} onOpenChange={setAgcPanel}
        title={editingAgc ? 'Modifier l\'agence' : 'Nouvelle agence'}
        footer={
          <div className="flex gap-3">
            <button onClick={saveAgc} disabled={saving}
              data-testid="save-agence-btn"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--primary-gradient)' }}>
              <Check size={14} /> {saving ? 'Enregistrement…' : (editingAgc ? 'Mettre à jour' : 'Créer')}
            </button>
            <button onClick={() => setAgcPanel(false)}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
              Annuler
            </button>
          </div>
        }>
        <div className="grid grid-cols-2 gap-4">
          {/* Logo */}
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Logo de l'agence</label>
            <LogoUploader
              value={agcForm.logo_b64}
              onChange={(b64) => setAgcForm(f => ({ ...f, logo_b64: b64 }))}
            />
          </div>
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Nom de l'agence *</label>
            <input value={agcForm.nom} onChange={e => setAgcForm(f => ({ ...f, nom: e.target.value }))}
              placeholder="Ex: Agence Paris Nord" className={inp} style={inpSt} data-testid="agence-nom-input" />
          </div>
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Responsable</label>
            <input value={agcForm.responsable} onChange={e => setAgcForm(f => ({ ...f, responsable: e.target.value }))}
              placeholder="Nom du responsable" className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Téléphone</label>
            <PhoneInput value={agcForm.telephone} onChange={e => setAgcForm(f => ({ ...f, telephone: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Email</label>
            <input type="email" value={agcForm.email} onChange={e => setAgcForm(f => ({ ...f, email: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div className="col-span-2">
            <label className={lbl} style={lblSt}>Adresse</label>
            <input value={agcForm.adresse} onChange={e => setAgcForm(f => ({ ...f, adresse: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Code postal</label>
            <input value={agcForm.code_postal} onChange={e => setAgcForm(f => ({ ...f, code_postal: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Ville</label>
            <input value={agcForm.ville} onChange={e => setAgcForm(f => ({ ...f, ville: e.target.value }))}
              className={inp} style={inpSt} />
          </div>
          {/* Intégration Odoo */}
          <div className="col-span-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <label className={lbl} style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                Société Odoo associée
                <span className="ml-1 font-normal opacity-70">(optionnel)</span>
              </label>
              <button type="button" onClick={loadOdooCompanies} disabled={odooLoading}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded-md border"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                <RefreshCw size={11} className={odooLoading ? 'animate-spin' : ''} />
                {odooLoading ? 'Chargement…' : 'Actualiser'}
              </button>
            </div>
            <select
              value={agcForm.odoo_company_id}
              onChange={e => setAgcForm(f => ({ ...f, odoo_company_id: e.target.value ? parseInt(e.target.value) : null }))}
              className={inp} style={inpSt}
              data-testid="agence-odoo-company-id-input">
              <option value="">— Aucune correspondance Odoo —</option>
              {odooCompanies.map(c => (
                <option key={c.id} value={c.id}>{c.name} (id: {c.id})</option>
              ))}
            </select>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>
              Si configuré, cette valeur est prioritaire sur la société parente pour l'export comptable.
            </p>
          </div>
          {/* Lien Annuaire */}
          <div className="col-span-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <label className={lbl} style={lblSt}>
              Entreprise annuaire liée
              <span className="ml-1 font-normal opacity-70">(optionnel)</span>
            </label>
            <EntreprisePicker
              value={agcForm.entreprise_id}
              valueNom={agcForm.entreprise_nom}
              onChange={(id, nom) => setAgcForm(f => ({ ...f, entreprise_id: id, entreprise_nom: nom }))}
            />
            {socForm.entreprise_id && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                <input type="checkbox"
                  checked={agcForm.filtrer_parc}
                  onChange={e => setAgcForm(f => ({ ...f, filtrer_parc: e.target.checked }))}
                  className="w-4 h-4 rounded cursor-pointer"
                  style={{ accentColor: 'var(--primary-solid)' }} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Filtrer le parc matériel sur cet exploitant
                </span>
              </label>
            )}
            <label className="flex items-center gap-2 mt-1.5 cursor-pointer select-none">
              <input type="checkbox"
                checked={agcForm.filtrer_controles}
                onChange={e => setAgcForm(f => ({ ...f, filtrer_controles: e.target.checked }))}
                className="w-4 h-4 rounded cursor-pointer"
                style={{ accentColor: 'var(--primary-solid)' }} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Filtrer les contrôles périodiques sur cette agence
              </span>
            </label>
            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>
              Si coché, prioritaire sur le paramètre de la société parente.
            </p>
          </div>
        </div>
      </SidePanel>
    </div>
  );
}
